import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    IVehicleDetail,
    IVehicleSelectItem,
    VehicleCreatePayload,
    VehicleRestoreDeletedPayload,
    VehicleUpdatePayload,
} from "@/customTypings/vehicle/Vehicle";
import { authHeader, handleResponse } from "../../utils";
import { postVehicleCreate } from "@/utils/vehicle/vehicleCreateConflict";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";

export const vehiclesService = {
    getById,
    select,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/vehicles/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IVehicleDetail>;
}

/** Active vehicles for the organisation (carrier pre-fill lookups). */
function select() {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/vehicles/select`, requestOptions).then(handleResponse) as Promise<IVehicleSelectItem[]>;
}

function create(payload: VehicleCreatePayload) {
    return postVehicleCreate<IVehicleDetail>(`/api/vehicles/create`, payload);
}

function restoreDeletedWithCreateValues(payload: VehicleRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/vehicles/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IVehicleDetail>;
}

function update(payload: VehicleUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/vehicles/update`, requestOptions).then(handleResponse) as Promise<
        UpdateMutationResult<IVehicleDetail>
    >;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(`/api/vehicles/${action}?id=${encodeURIComponent(id)}`, requestOptions).then(handleResponse);
}
